import KnowledgeBase from "../../../classes/KnowledgeBase.js";
import { db } from "../../../boot/firebase";
import {
  collection,
  deleteDoc,
  doc,
  setDoc,
  updateDoc,
} from "firebase/firestore";

// ============================================
// KNOWLEDGE BASE ENTRIES
// ============================================

export const GET_KNOWLEDGE_BASE_ENTRY = async ({ dispatch }, { entryID }) => {
  return dispatch("GET_DOC", {
    converter: KnowledgeBase,
    path: `knowledgeBase/${entryID}`,
    stateKey: "knowledgeBase",
    id: entryID,
  });
};

export const GET_KNOWLEDGE_BASE_ENTRIES = async (
  { dispatch },
  { where, limitCount, pagination } = {}
) => {
  try {
    return await dispatch("GET_COLLECTION", {
      converter: KnowledgeBase,
      stateKey: "knowledgeBase",
      path: "knowledgeBase",
      where,
      limitCount,
      pagination,
    });
  } catch (error) {
    // Collection may not exist yet - return empty result
    console.warn("Knowledge base collection not found or error:", error);
    return [];
  }
};

export const LISTEN_KNOWLEDGE_BASE = async (
  { dispatch },
  { where, listenerPath, error, next, unlisten } = {}
) => {
  return dispatch("LISTEN_COLLECTION", {
    converter: KnowledgeBase,
    stateKey: "knowledgeBase",
    path: "knowledgeBase",
    where: where || [["isPublished", "==", true]],
    listenerPath: listenerPath || "knowledgeBase",
    error,
    next,
    unlisten,
  });
};

export const LISTEN_KNOWLEDGE_BASE_ENTRY = async (
  { dispatch },
  { entryID, next, error, unlisten }
) => {
  return dispatch("LISTEN_DOC", {
    converter: KnowledgeBase,
    path: `knowledgeBase/${entryID}`,
    stateKey: "knowledgeBase",
    id: entryID,
    next,
    error,
    unlisten,
  });
};

export const SAVE_KNOWLEDGE_BASE_ENTRY = async (
  { state, commit, dispatch },
  { entry, merge = true }
) => {
  // Dates, not epoch numbers: every sibling action stamps `new Date()` and the
  // KnowledgeBase converter runs timestampToDate() on read.
  const now = new Date();
  const user = state.user;

  // Set metadata. User has `.id` — there is no `.uid`, and an undefined field
  // makes setDoc throw (ignoreUndefinedProperties is not enabled).
  if (!entry.id) {
    entry.data.created = now;
    entry.data.createdBy = user.id;
  }
  entry.data.modified = now;
  entry.data.modifiedBy = user.id;

  // Save to Firestore
  const docRef = entry.id
    ? doc(db, `knowledgeBase/${entry.id}`)
    : doc(collection(db, "knowledgeBase"));

  // Upload new photos and delete removed ones. This rewrites each photo's
  // `src` from a data URL to its download URL, so it has to run before the
  // doc write — otherwise the base64 payload lands in Firestore.
  await dispatch("SAVE_IMAGES", {
    parentData: entry.data,
    existingData: state.knowledgeBase[docRef.id]?.data,
    fieldPath: "photos",
    folderPath: `knowledgeBase/${docRef.id}/photos`,
  });

  await setDoc(docRef, entry.data, { merge });

  // Update local state
  const savedEntry = new KnowledgeBase(entry.data, docRef.id);
  commit("SET", {
    stateKey: "knowledgeBase",
    stateID: docRef.id,
    doc: savedEntry,
  });

  return savedEntry;
};

export const DELETE_KNOWLEDGE_BASE_ENTRY = async (
  { state, commit, dispatch },
  { entryID }
) => {
  // Drop the entry's photos from Storage first; once the doc is gone their ids
  // are unrecoverable and the files would be orphaned.
  const existingData = state.knowledgeBase[entryID]?.data;
  if (existingData?.photos?.length) {
    await dispatch("SAVE_IMAGES", {
      parentData: { photos: [] },
      existingData,
      fieldPath: "photos",
      folderPath: `knowledgeBase/${entryID}/photos`,
    });
  }

  await deleteDoc(doc(db, `knowledgeBase/${entryID}`));

  // Remove from local state
  commit("REMOVE", { stateKey: "knowledgeBase", stateID: entryID });

  return true;
};

// ============================================
// KNOWLEDGE BASE QUERIES
// ============================================

export const GET_KNOWLEDGE_BASE_FOR_ISSUE_TYPE = async (
  { state, dispatch },
  { issueType, productType }
) => {
  // Build query
  const where = [["isPublished", "==", true]];

  if (issueType) {
    where.push(["issueTypes", "array-contains", issueType]);
  }

  // Fetch entries
  await dispatch("GET_KNOWLEDGE_BASE_ENTRIES", { where });

  // Filter and sort results
  const entries = Object.values(state.knowledgeBase || {}).filter((entry) => {
    if (!entry.data.isPublished) return false;
    if (issueType && !entry.data.issueTypes?.includes(issueType)) return false;
    if (
      productType &&
      entry.data.productType &&
      entry.data.productType !== productType
    ) {
      return false;
    }
    return true;
  });

  // Sort by pinned first, then sort order
  return entries.sort((a, b) => {
    if (a.data.isPinned !== b.data.isPinned) {
      return a.data.isPinned ? -1 : 1;
    }
    return (a.data.sortOrder || 0) - (b.data.sortOrder || 0);
  });
};

export const SEARCH_KNOWLEDGE_BASE = async (
  { state, dispatch },
  { query, filters = {} }
) => {
  const { category, productType, issueType, includeInternal } = filters;

  // Build query
  const where = [["isPublished", "==", true]];

  if (category) {
    where.push(["category", "==", category]);
  }
  if (productType) {
    where.push(["productType", "==", productType]);
  }

  // Fetch entries
  await dispatch("GET_KNOWLEDGE_BASE_ENTRIES", { where });

  // Filter and search locally (Firestore doesn't support full-text search)
  const searchLower = (query || "").toLowerCase();
  const entries = Object.values(state.knowledgeBase || {}).filter((entry) => {
    if (!entry.data.isPublished) return false;
    if (entry.data.isInternal && !includeInternal) return false;
    if (category && entry.data.category !== category) return false;
    if (productType && entry.data.productType !== productType) return false;
    if (issueType && !entry.data.issueTypes?.includes(issueType)) return false;

    // Text search
    if (searchLower) {
      const title = (entry.data.title || "").toLowerCase();
      const content = (entry.data.content || "").toLowerCase();
      const summary = (entry.data.summary || "").toLowerCase();
      const tags = (entry.data.tags || []).join(" ").toLowerCase();

      if (
        !title.includes(searchLower) &&
        !content.includes(searchLower) &&
        !summary.includes(searchLower) &&
        !tags.includes(searchLower)
      ) {
        return false;
      }
    }

    return true;
  });

  // Score each entry WITHOUT touching it. `{ ...entry, relevanceScore }` looks
  // harmless but spreads a KnowledgeBase instance into a plain object, so the
  // prototype — and with it every getter (categoryType, productCategoryType,
  // hasMedia, photoCount, hasVideo, isNew, createdDate, modifiedDate) —
  // disappears from the results. The docs in state are deepFrozen by the SET
  // mutation too, so the score cannot be hung off the instance either. Keep
  // the scores beside the entries and return the instances themselves.
  const relevanceScore = (entry) => {
    let score = 0;
    if (entry.data.isPinned) score += 10;
    if (searchLower) {
      const title = (entry.data.title || "").toLowerCase();
      const summary = (entry.data.summary || "").toLowerCase();
      if (title.includes(searchLower)) score += 8;
      if (summary.includes(searchLower)) score += 4;
    }
    return score;
  };

  const scores = new Map(
    entries.map((entry) => [entry, relevanceScore(entry)])
  );

  // Sort by relevance, then by the author-defined order
  return entries.sort((a, b) => {
    if (scores.get(b) !== scores.get(a)) {
      return scores.get(b) - scores.get(a);
    }
    return (a.data.sortOrder || 0) - (b.data.sortOrder || 0);
  });
};

// ============================================
// KNOWLEDGE BASE FEEDBACK
// ============================================

export const MARK_KNOWLEDGE_BASE_HELPFUL = async (
  { state, commit },
  { entryID, isHelpful }
) => {
  const entry = state.knowledgeBase[entryID];
  if (!entry) return;

  const field = isHelpful ? "helpfulCount" : "notHelpfulCount";
  const currentCount = entry.data[field] || 0;

  await updateDoc(doc(db, `knowledgeBase/${entryID}`), {
    [field]: currentCount + 1,
  });

  // Update local state
  const updatedEntry = new KnowledgeBase(
    {
      ...entry.data,
      [field]: currentCount + 1,
    },
    entryID
  );

  commit("SET", {
    stateKey: "knowledgeBase",
    stateID: entryID,
    doc: updatedEntry,
  });

  return updatedEntry;
};

export const INCREMENT_KNOWLEDGE_BASE_VIEW = async (
  { state, commit },
  { entryID }
) => {
  const entry = state.knowledgeBase[entryID];
  if (!entry) return;

  const currentCount = entry.data.viewCount || 0;

  await updateDoc(doc(db, `knowledgeBase/${entryID}`), {
    viewCount: currentCount + 1,
  });

  // Update local state
  const updatedEntry = new KnowledgeBase(
    {
      ...entry.data,
      viewCount: currentCount + 1,
    },
    entryID
  );

  commit("SET", {
    stateKey: "knowledgeBase",
    stateID: entryID,
    doc: updatedEntry,
  });

  return updatedEntry;
};
